import { X } from 'lucide-react';
import { Box, Flex, Text } from '@chakra-ui/react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'default' | 'danger';
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.4)"
      align="center"
      justify="center"
      zIndex={9999}
      p="5"
      onClick={handleBackdropClick}
    >
      <Box
        bg="white"
        borderRadius="2xl"
        boxShadow="lg"
        maxW="md"
        w="full"
        p={{ base: '6', md: '8' }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Flex align="flex-start" justify="space-between" mb="4">
          <Text
            as="h3"
            fontSize={{ base: 'lg', md: 'xl' }}
            fontWeight="semibold"
            color="volyx.text"
            m="0"
          >
            {title}
          </Text>
          <Box
            as="button"
            p="1.5"
            bg="transparent"
            border="none"
            cursor="pointer"
            borderRadius="md"
            transition="all 0.2s"
            _hover={{ bg: 'volyx.lightGray' }}
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={20} color="#8E8E93" />
          </Box>
        </Flex>

        <Text
          fontSize={{ base: 'sm', md: 'md' }}
          color="volyx.textSecondary"
          mb="6"
        >
          {message}
        </Text>

        <Flex gap="3">
          <Box
            as="button"
            flex="1"
            px="4"
            py="2.5"
            bg="volyx.lightGray"
            color="volyx.text"
            borderRadius="lg"
            fontWeight="medium"
            border="none"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ bg: 'volyx.mediumGray' }}
            onClick={onCancel}
          >
            {cancelText}
          </Box>
          <Box
            as="button"
            flex="1"
            px="4"
            py="2.5"
            borderRadius="lg"
            fontWeight="medium"
            border="none"
            cursor="pointer"
            transition="all 0.2s"
            color="white"
            bg={variant === 'danger' ? '#ef4444' : 'volyx.primary'}
            _hover={{
              bg: variant === 'danger' ? '#dc2626' : 'rgba(31,41,50,0.9)',
            }}
            onClick={onConfirm}
          >
            {confirmText}
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}
